// src/components/ElearningStudentLoginPage.jsx
import React, { useState, useCallback, useEffect } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import elearningApi from 'api/elearningApi';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import drivingApi from 'api/drivingApi';
import { Image } from 'react-bootstrap';
import { FaEye, FaEyeSlash, FaGraduationCap } from 'react-icons/fa';

function ElearningStudentLoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const hostname = window.location.hostname;
  const subdomain = hostname.split('.')[0];
  const [center, setCenter] = useState(null);

  useEffect(() => {
    if (subdomain && subdomain !== 'lms') {
      drivingApi
        .queryDrivingCenters({
          filter: { shortName: subdomain },
          page: 1,
        })
        .then((response) => {
          setCenter(response?.data?.[0] || null);
          localStorage.setItem('center', JSON.stringify(response?.data?.[0]));
        })
        .catch((error) => {
          console.error('Lỗi khi lấy thông tin trung tâm:', error);
        });
    }
  }, [subdomain]);

  const navigate = useNavigate();

  // Login now routes through the backend proxy (`POST /elearning/login`).
  // The Moodle wstoken stays server-side; the browser only ever sees the
  // sessionToken JWT. Phase 2 will remove `moodleToken` from localStorage
  // entirely once book/video/forum pages also proxy through the backend.
  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setError(null);
      setIsLoading(true);

      try {
        // axiosClient response interceptor unwraps `{ message, data }` →
        // `data` arrives as the inner payload `{ sessionToken, refreshToken, user }`.
        const data = await elearningApi.studentLogin(username, password);
        const sessionToken = data?.sessionToken;
        const refreshToken = data?.refreshToken;
        const user = data?.user;
        if (!sessionToken || !user) {
          throw new Error('Phản hồi đăng nhập không hợp lệ.');
        }

        // Phase 2D step 5: BE no longer returns moodleToken. Wipe any
        // stale moodleToken + moodleSiteInfo from a previous old-FE login
        // so this tab doesn't carry credentials it doesn't need anymore.
        localStorage.setItem('sessionToken', sessionToken);
        // refreshToken có thể null nếu BE chưa cấu hình ELEARNING_REFRESH_TOKEN_SECRET
        // (graceful degrade) — chỉ save khi BE trả về thật.
        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken);
        } else {
          localStorage.removeItem('refreshToken');
        }
        localStorage.removeItem('moodleToken');
        localStorage.setItem('forcePasswordChange', user.forcePasswordChange || '0');
        // Keep a lightweight cache of the moodle profile for any code path
        // that still reads `moodleSiteInfo` (Phase 2 removes this).
        localStorage.setItem(
          'moodleSiteInfo',
          JSON.stringify({
            userid: user.elearningUserId,
            fullname: user.fullname,
            username: user.username,
          })
        );

        // Login = hành động con người → cờ takeover one-shot để lần
        // socket connect đầu tiên GIÀNH quyền học từ thiết bị/tab khác
        // (session-ownership). Giữ đúng ngữ nghĩa last-login-wins cũ;
        // khác biệt: reconnect TỰ ĐỘNG của thiết bị cũ sau đó không mang
        // cờ nên không giành lại được (hết kick war). Key khớp
        // useElearningSession.TAKEOVER_FLAG_KEY.
        try { sessionStorage.setItem('elearning-session-takeover', '1'); } catch (_) { /* noop */ }

        if (user.forcePasswordChange === '1') {
          navigate(PATH.ELEARNING.CHANGE_PASSWORD);
        } else {
          window.location.href = PATH.ELEARNING.STUDENT.ROOT;
        }
        // Success path → không reset isLoading. Component sẽ unmount khi
        // navigate / window.location.href chạy. Giữ disabled tới lúc đó.
      } catch (err) {
        // Backend returns `{ message, data: { code } }` for known failures.
        const apiMessage = err?.response?.data?.message;
        setError(apiMessage || err.message || 'Đã xảy ra lỗi trong quá trình đăng nhập.');
      } finally {
        setIsLoading(false);
      }
    },
    [username, password, navigate]
  );

  return (
    <Container className='mt-5'>
      {center && (
        <div className='text-center mb-4'>
          <Image
            src={center.logo}
            alt='Logo'
            className='mb-2'
            width={110}
            height={110}
            style={{ objectFit: 'contain' }}
          />
          <h6>{center.name}</h6>
        </div>
      )}
      <Row className='justify-content-center'>
        <Col md={8} lg={6} xl={5}>
          <Card className='shadow-sm'>
            <Card.Body className='p-4'>
              <div className="text-center mb-4">
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                  style={{ width: 56, height: 56, backgroundColor: '#e8f0fe' }}
                >
                  <FaGraduationCap size={28} color="#1a73e8" />
                </div>
                <h4 className="fw-bold mb-0">Đăng nhập LMS</h4>
              </div>
              <Form onSubmit={handleSubmit}>
                {error && (
                  <Alert
                    variant='danger'
                    onClose={() => setError(null)}
                    dismissible
                  >
                    {error}
                  </Alert>
                )}
                <Form.Group className='mb-3' controlId='loginUsername'>
                  <Form.Label>
                    Tên đăng nhập <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type='text'
                    placeholder='Nhập tên đăng nhập'
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </Form.Group>
                <Form.Group className='mb-3' controlId='loginPassword'>
                  <Form.Label>
                    Mật khẩu <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      placeholder='Nhập mật khẩu'
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={isLoading}
                    />
                    <Button
                      variant="link"
                      className="position-absolute top-50 end-0 translate-middle-y pe-3"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ border: 'none' }}
                    >
                      {showPassword ? <FaEye size={18} /> : <FaEyeSlash size={18} />}
                    </Button>
                  </div>
                </Form.Group>
                <div className='d-grid'>
                  <Button variant='primary' type='submit' disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Spinner
                          as='span'
                          animation='border'
                          size='sm'
                          role='status'
                          aria-hidden='true'
                          className='me-1'
                        />
                        Đang xử lý...
                      </>
                    ) : (
                      'Đăng nhập'
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default ElearningStudentLoginPage;
